"use client";

import React, { useState, useEffect, useRef } from "react";
import ImageDimensions from "./ImageDimensions";
import SaveButton, { galleryActionPillClassName } from "./SaveButton";
import GalleryZoomModal from "./GalleryZoomModal";
import { MeshGradient } from "@paper-design/shaders-react";

interface GalleryImage {
  imageId: string;
  imageData: string;
  timestamp: number;
  isProcessing?: boolean;
}

interface GalleryCarouselProps {
  galleryImages: GalleryImage[];
  currentGalleryIndex: number;
  setCurrentGalleryIndex: (index: number) => void;
  isProcessing: boolean;
  originalImage: string | null;
  showPrevious: boolean;
  getPreviousImage: () => string | null;
  getCurrentDisplayedImage: () => string;
  savedImageIds?: Set<string>;
  onSaveComplete?: (fileId: string, saved: boolean) => void;
  onEditWithChat?: () => void;
}

export default function GalleryCarousel({
  galleryImages,
  currentGalleryIndex,
  setCurrentGalleryIndex,
  isProcessing,
  originalImage,
  showPrevious,
  getPreviousImage,
  getCurrentDisplayedImage,
  savedImageIds = new Set(),
  onSaveComplete,
  onEditWithChat,
}: GalleryCarouselProps) {
  const [processingElapsedTime, setProcessingElapsedTime] = useState(0);
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  const dragStartX = useRef(0);
  const dragStartY = useRef(0);
  const lastScrollTime = useRef(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const didSwipe = useRef(false);

  // Track processing elapsed time
  useEffect(() => {
    if (!isProcessing) {
      setProcessingElapsedTime(0);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = (Date.now() - startTime) / 1000;
      setProcessingElapsedTime(elapsed);
    }, 100);

    return () => clearInterval(interval);
  }, [isProcessing]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (zoomSrc) return;
      if (!galleryImages || galleryImages.length <= 1) return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setCurrentGalleryIndex(Math.max(0, currentGalleryIndex - 1));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setCurrentGalleryIndex(Math.min(galleryImages.length - 1, currentGalleryIndex + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryImages, currentGalleryIndex, setCurrentGalleryIndex, zoomSrc]);

  // Mouse scroll navigation with NON-PASSIVE event listener
  useEffect(() => {
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleWheel = (e: WheelEvent) => {
      if (zoomSrc) return;
      if (!galleryImages || galleryImages.length <= 1) return;

      // ALWAYS prevent page scroll when hovering gallery
      e.preventDefault();

      // Throttle to 500ms between navigations
      const now = Date.now();
      if (now - lastScrollTime.current < 500) return;
      lastScrollTime.current = now;

      if (e.deltaY > 0) {
        // Scroll down -> next image
        setCurrentGalleryIndex(Math.min(galleryImages.length - 1, currentGalleryIndex + 1));
      } else if (e.deltaY < 0) {
        // Scroll up -> previous image
        setCurrentGalleryIndex(Math.max(0, currentGalleryIndex - 1));
      }
    };

    // Add listener with { passive: false } to allow preventDefault()
    carousel.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      carousel.removeEventListener('wheel', handleWheel);
    };
  }, [galleryImages, currentGalleryIndex, setCurrentGalleryIndex, zoomSrc]);

  // Swipe handlers
  const handleDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    if (!galleryImages || galleryImages.length <= 1) return;

    didSwipe.current = false;
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    dragStartX.current = clientX;
    dragStartY.current = clientY;
  };

  const handleDragEnd = (e: React.MouseEvent | React.TouchEvent) => {
    if (!galleryImages || galleryImages.length <= 1) return;
    if (dragStartX.current === 0) return;

    const clientX = 'changedTouches' in e ? e.changedTouches[0].clientX : (e as React.MouseEvent).clientX;
    const clientY = 'changedTouches' in e ? e.changedTouches[0].clientY : (e as React.MouseEvent).clientY;

    const deltaX = clientX - dragStartX.current;
    const deltaY = clientY - dragStartY.current;

    // Only trigger if horizontal swipe (not vertical scroll)
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      didSwipe.current = true;
      if (deltaX > 0) {
        setCurrentGalleryIndex(Math.max(0, currentGalleryIndex - 1));
      } else {
        setCurrentGalleryIndex(Math.min(galleryImages.length - 1, currentGalleryIndex + 1));
      }
    }

    dragStartX.current = 0;
    dragStartY.current = 0;
  };

  const openZoomForImage = (imageSrc: string) => {
    if (!imageSrc || didSwipe.current) return;
    setZoomSrc(imageSrc);
  };

  const currentSrc =
    showPrevious && getPreviousImage()
      ? getPreviousImage()!
      : getCurrentDisplayedImage() || originalImage || "";

  const currentImage = galleryImages?.[currentGalleryIndex];
  const showActionPills =
    !!currentImage && !currentImage.isProcessing;
  const isResultSlide =
    !!galleryImages &&
    currentGalleryIndex < galleryImages.length - 1;

  const renderActionPills = (onOverlayClick?: (e: React.MouseEvent) => void) => {
    if (!showActionPills || !currentImage) return null;
    return (
      <div className="flex items-center justify-center gap-2" onClick={onOverlayClick}>
        <button
          type="button"
          onClick={() => {
            const src =
              showPrevious && getPreviousImage()
                ? getPreviousImage()!
                : currentImage.imageData;
            openZoomForImage(src);
          }}
          className={galleryActionPillClassName}
          title="Zoom"
          aria-label="Zoom image"
        >
          <svg
            className="w-4 h-4 shrink-0"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M21 21l-5.2-5.2M10.5 18a7.5 7.5 0 100-15 7.5 7.5 0 000 15zM10.5 7v7M7 10.5h7"
            />
          </svg>
          <span>Zoom</span>
        </button>

        {isResultSlide && onEditWithChat && (
          <button
            type="button"
            onClick={onEditWithChat}
            className={galleryActionPillClassName}
            title="Edit with Chat"
            aria-label="Edit with Chat"
          >
            <svg
              className="w-4 h-4 shrink-0"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z"
              />
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v18" />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3l8 4.5-8 4.5-8-4.5L12 3z"
              />
            </svg>
            <span>AI Edit</span>
          </button>
        )}

        {isResultSlide && (
          <SaveButton
            fileId={currentImage.imageId}
            initialSaved={savedImageIds.has(currentImage.imageId)}
            onSaveComplete={(saved) => onSaveComplete?.(currentImage.imageId, saved)}
          />
        )}
      </div>
    );
  };

  return (
    <div className="w-full h-full min-h-0 flex flex-col items-center">
      {/* Desktop: action pills hug top of scene (above the image stage) */}
      {showActionPills && (
        <div className="hidden md:flex flex-shrink-0 w-full justify-center pt-0 pb-2 z-20">
          {renderActionPills()}
        </div>
      )}

      {/* Arrows + stage — fills remaining height above metadata */}
      <div className="relative w-full flex-1 min-h-0 flex items-center justify-center md:gap-4">
        {galleryImages && galleryImages.length > 0 && (
          <button
            type="button"
            onClick={() => setCurrentGalleryIndex(Math.max(0, currentGalleryIndex - 1))}
            disabled={currentGalleryIndex === 0}
            className="absolute md:relative left-2 md:left-auto flex-shrink-0 p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all z-30"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        <div
          ref={carouselRef}
          className="relative w-full h-full min-h-0 max-w-4xl overflow-hidden"
          data-image-container
          onMouseDown={handleDragStart}
          onMouseUp={handleDragEnd}
          onTouchStart={handleDragStart}
          onTouchEnd={handleDragEnd}
        >
          {galleryImages &&
            galleryImages.map((image, index) => {
              const offset = index - currentGalleryIndex;
              return (
                <div
                  key={image.imageId}
                  className="absolute inset-0 flex items-center justify-center min-h-0 transition-all duration-500 ease-out cursor-pointer p-1"
                  style={{
                    transform:
                      offset === 0
                        ? "translateX(0)"
                        : offset < 0
                          ? "translateX(calc(-100% - 24px))"
                          : "translateX(calc(100% + 24px))",
                    opacity: offset === 0 ? 1 : Math.abs(offset) === 1 ? 0.35 : 0,
                    zIndex: offset === 0 ? 10 : 5,
                    pointerEvents: image.isProcessing ? "none" : "auto",
                  }}
                  onClick={() => {
                    if (image.isProcessing) return;
                    if (offset !== 0) {
                      setCurrentGalleryIndex(index);
                      return;
                    }
                    const src =
                      showPrevious && getPreviousImage()
                        ? getPreviousImage()!
                        : image.imageData;
                    openZoomForImage(src);
                  }}
                >
                  <div className="relative h-full w-full min-h-0 flex items-center justify-center">
                    {/* Mobile: pills over image bottom */}
                    {offset === 0 && !image.isProcessing && (
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 md:hidden">
                        {renderActionPills((e) => e.stopPropagation())}
                      </div>
                    )}

                    {image.isProcessing ? (
                      <div
                        className="w-full max-w-md aspect-square max-h-full rounded-lg overflow-hidden relative shadow-2xl md:max-w-none md:w-auto md:h-[90%] md:aspect-[3/4]"
                        style={{
                          backgroundColor: "#5956E9",
                          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1799 1799' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'%3E%3Canimate attributeName='seed' values='1;10;20;30;40;50;60;70;80;90;100' dur='5s' repeatCount='indefinite'/%3E%3C/feTurbulence%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E"), radial-gradient(circle at 20% 30%, #7c3aed 3%, transparent 76%), radial-gradient(circle at 80% 20%, #a855f7 1%, transparent 86%), radial-gradient(circle at 70% 60%, #6366f1 7%, transparent 76%), radial-gradient(circle at 30% 80%, #8b5cf6 7%, transparent 87%), radial-gradient(circle at 90% 70%, #7c3aed 15%, transparent 81%), radial-gradient(circle at 10% 10%, #a855f7 1%, transparent 68%)`,
                          backgroundBlendMode:
                            "overlay, normal, normal, normal, normal, normal, normal",
                        }}
                      >
                        <div className="absolute inset-0 overflow-hidden opacity-60 [&_canvas]:!h-full [&_canvas]:!w-full [&_canvas]:!max-h-none [&_canvas]:!max-w-none">
                          <MeshGradient
                            width={720}
                            height={1280}
                            colors={["#5956E9", "#7c3aed", "#a855f7", "#F163C2"]}
                            distortion={0.8}
                            swirl={0.1}
                            grainMixer={0}
                            grainOverlay={0.5}
                            speed={1}
                          />
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                          <div className="text-center max-w-sm mx-auto p-6">
                            <svg className="animate-spin h-16 w-16 text-white mx-auto mb-6" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                              <path className="opacity-75" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M12 2a10 10 0 0110 10" />
                            </svg>
                            <p className="text-white text-lg font-medium mb-2">
                              {processingElapsedTime < 2 ? "Preparing..." : "Generating..."}
                            </p>
                            <p className="text-white text-sm font-normal mb-4" style={{ minHeight: "1.25rem" }}>
                              {processingElapsedTime >= 2
                                ? `${(processingElapsedTime - 2).toFixed(2)}s`
                                : "--:--"}
                            </p>
                            <div className="inline-flex items-center bg-white bg-opacity-20 backdrop-blur-sm rounded-full px-4 py-2">
                              <svg className="w-4 h-4 text-white mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-white text-sm">Average wait time: ~10-20 seconds</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={
                          showPrevious && offset === 0 && getPreviousImage()
                            ? getPreviousImage()!
                            : image.imageData
                        }
                        alt={`Result ${index + 1}`}
                        className="max-h-full max-w-full w-auto h-auto object-contain rounded-lg shadow-2xl"
                        draggable={false}
                      />
                    )}
                  </div>
                </div>
              );
            })}
        </div>

        {galleryImages && galleryImages.length > 0 && (
          <button
            type="button"
            onClick={() =>
              setCurrentGalleryIndex(Math.min(galleryImages.length - 1, currentGalleryIndex + 1))
            }
            disabled={currentGalleryIndex >= galleryImages.length - 1}
            className="absolute md:relative right-2 md:right-auto flex-shrink-0 p-3 bg-white rounded-full shadow-lg hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-all z-30"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
      </div>

      {galleryImages && galleryImages.length > 0 && (
          <div className="hidden md:flex flex-shrink-0 py-1 min-h-[1.75rem] w-full justify-end">
            {galleryImages[currentGalleryIndex]?.isProcessing || !currentSrc ? (
              <div className="text-gray-300 text-xs mt-2 mr-4 font-mono" aria-hidden>
                — × —px
              </div>
            ) : (
              <ImageDimensions imageData={getCurrentDisplayedImage()} />
            )}
          </div>
        )}

      <GalleryZoomModal src={zoomSrc} onClose={() => setZoomSrc(null)} />
    </div>
  );
}

